import React, { useState } from 'react';
import './Privacy.css';
import {
  InformationCircleIcon,
  FolderOpenIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ShareIcon,
  LockClosedIcon,
  GlobeAltIcon,
  LinkIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  HandRaisedIcon,
  PauseCircleIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const SECTIONS = [
  {
    id: '1',
    title: 'Introduction',
    icon: InformationCircleIcon,
    content:
      'Welcome to SnapConX. We are committed to protecting your personal information and your right to privacy in accordance with POPIA and other applicable South African laws.',
  },
  {
    id: '2',
    title: 'Information We Collect',
    icon: FolderOpenIcon,
    content:
      'We may collect account information, profile information, device information, usage data, and payment-related information.',
  },
  {
    id: '3',
    title: 'How We Use Your Information',
    icon: Cog6ToothIcon,
    content:
      'We use your information to provide services, manage accounts, improve the app, process transactions, and comply with legal obligations.',
  },
  {
    id: '4',
    title: 'Lawful Basis for Processing',
    icon: ShieldCheckIcon,
    content:
      'Under POPIA, we process personal information based on consent, legal obligations, contracts, and legitimate interests.',
  },
  {
    id: '5',
    title: 'Information Sharing & Disclosure',
    icon: ShareIcon,
    content:
      'We may share information with service providers, regulators, or legal authorities where required.',
  },
  {
    id: '6',
    title: 'Optional Google and Outlook Calendar Connections',
    icon: LinkIcon,
    content: [
      'Calendar connections are optional. When you connect Google Calendar or Microsoft Outlook, you authorize SnapConX to access your calendar account for appointment synchronization. You can connect either provider or both. Personal and business connections are managed separately; a business connection requires the business owner’s authorization.',
      'We access your calendar account identifier and email address, available calendar identifiers and names, and whether calendars are writable. We request permission to manage events in your owned Google calendars or writable Outlook calendars so you can select a destination. These permissions can cover more events than SnapConX uses: the integration manages events associated with SnapConX bookings and does not import your unrelated appointments into SnapConX.',
      'When you select a calendar and enable synchronization, we send eligible appointment details to the provider: service and business names, start and end times, business location where available, and a SnapConX booking reference. We create, update, retrieve and remove managed events as needed. This is a copy of the booking; edits made directly in Google or Outlook do not update the SnapConX appointment.',
    ],
  },
  {
    id: '7',
    title: 'Calendar Data Storage and Protection',
    icon: LockClosedIcon,
    content: [
      'SnapConX stores calendar connection settings, account and calendar identifiers, the selected calendar name, managed-event identifiers, and synchronization status in Firebase/Google Cloud. We store provider access and refresh tokens encrypted on the server so synchronization can continue when you are not using the app. Provider passwords are entered with Google or Microsoft and are not collected by SnapConX.',
      'Server authorization checks restrict who can manage a connection, and client applications cannot directly read stored calendar tokens. Calendar requests use HTTPS. Google or Microsoft receives the appointment details needed to create events; Firebase/Google Cloud processes connection and synchronization data to operate this feature. These providers may process data outside South Africa.',
    ],
  },
  {
    id: '8',
    title: 'Calendar Data Use and Google Limited Use',
    icon: ShieldCheckIcon,
    content: [
      'Calendar account data is used to provide the calendar connection and appointment synchronization you request, and to diagnose or secure that feature. We do not sell calendar data, use it for advertising, or use it to train generalized artificial intelligence models. Connecting one provider does not copy your unrelated events into another provider.',
      <>SnapConX’s use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including its Limited Use requirements. Human access to Google user data is limited to circumstances permitted by that policy, such as your affirmative permission, necessary security investigation, or applicable legal requirements.</>,
    ],
  },
  {
    id: '9',
    title: 'Disconnecting, Retention and Deletion',
    icon: TrashIcon,
    content: [
      'You can disable synchronization or select Disconnect in Calendar connections. Disabling synchronization stops creating or updating appointment copies and may remove existing SnapConX-managed event copies during cleanup. Disconnecting disables synchronization and queues removal of managed calendar events. It does not cancel the original booking in SnapConX or remove unrelated calendar events.',
      'Credentials are retained while a connection is active and temporarily during disconnect cleanup. After managed-event cleanup finishes, SnapConX deletes the stored tokens and clears the connected account email, account identifier and selected calendar details. Minimal connection and synchronization records may remain. Cleanup can be delayed if the provider is unavailable or access has been revoked; if removal cannot finish, contact support for help with deletion. You can also revoke SnapConX access in your Google or Microsoft account settings, but revoking access first may prevent automated removal of calendar events.',
      'Disconnecting a calendar does not delete booking, payment or other SnapConX account records. To request access, correction or deletion of personal information, contact info@dotanddash.co.za. Records needed for legal obligations or resolving disputes may be retained for those purposes.',
    ],
  },
];

const RIGHTS = [
  { icon: EyeIcon, label: 'Access' },
  { icon: PencilSquareIcon, label: 'Rectify' },
  { icon: TrashIcon, label: 'Erase' },
  { icon: HandRaisedIcon, label: 'Object' },
  { icon: PauseCircleIcon, label: 'Restrict' },
  { icon: ArrowDownTrayIcon, label: 'Portability' },
];

export default function DataPrivacyScreen() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="privacy-container">
      {/* Header */}
      <div className="privacy-header">
        <button className="icon-button">
          <ArrowLeftIcon className="icon" />
        </button>

        <h1 className="privacy-header-title">Data & Privacy</h1>

        <button
          className="icon-button"
          onClick={() => setShowModal(true)}
        >
          <PhoneIcon className="icon" />
        </button>
      </div>

      <div className="privacy-content">
        {/* Banner */}
        <div className="privacy-banner">
          <div className="privacy-banner-icon">
            <ShieldCheckIcon className="large-icon" />
          </div>

          <div>
            <h2>Your Privacy Matters</h2>
            <p>Protected under POPIA — Last Updated: 14 October 2026</p>
          </div>
        </div>

        {/* Rights */}
        <div className="privacy-card">
          <h3>Your Rights at a Glance</h3>

          <div className="rights-grid">
            {RIGHTS.map((right) => {
              const Icon = right.icon;

              return (
                <div className="right-item" key={right.label}>
                  <div className="right-icon">
                    <Icon className="icon" />
                  </div>

                  <span>{right.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sections */}
        <h3 className="section-heading">Privacy Policy Details</h3>

        {SECTIONS.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;

          return (
            <div className="section-card" key={section.id}>
              <button
                className="section-header"
                onClick={() => toggleSection(section.id)}
              >
                <div className="section-left">
                  <div className="section-icon">
                    <Icon className="icon" />
                  </div>

                  <span className="section-title">
                    {section.title}
                  </span>
                </div>

                {isExpanded ? (
                  <ChevronUpIcon className="icon small-grey" />
                ) : (
                  <ChevronDownIcon className="icon small-grey" />
                )}
              </button>

              {isExpanded && (
                <div className="section-body">
                  {(Array.isArray(section.content) ? section.content : [section.content]).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* CTA */}
        <button
          className="contact-cta"
          onClick={() => setShowModal(true)}
        >
          <div className="cta-left">
            <EnvelopeIcon className="icon" />
            <span>Contact Us or Lodge a Complaint</span>
          </div>

          <ChevronRightIcon className="icon" />
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Contact & Regulator Info</h2>

              <button onClick={() => setShowModal(false)}>
                <XMarkIcon className="icon" />
              </button>
            </div>

            <div className="modal-body">
              <div className="contact-block">
                <h3>Contact Us</h3>

                <p>Dot and Dash Trading CC</p>

                <a href="mailto:info@dotanddash.co.za">
                  <EnvelopeIcon className="icon" />
                  info@dotanddash.co.za
                </a>
              </div>

              <hr />

              <div className="contact-block">
                <h3>Information Regulator</h3>

                <a
                  href="https://www.justice.gov.za/inforeg/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <GlobeAltIcon className="icon" />
                  www.justice.gov.za/inforeg
                </a>

                <a href="mailto:inforeg@justice.gov.za">
                  <EnvelopeIcon className="icon" />
                  inforeg@justice.gov.za
                </a>

                <a href="mailto:complaints.IR@justice.gov.za">
                  <ExclamationCircleIcon className="icon" />
                  complaints.IR@justice.gov.za
                </a>

                <div className="address-row">
                  <MapPinIcon className="icon" />

                  <span>
                    JD House, 27 Stiemens Street, Braamfontein,
                    Johannesburg, 2001
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
